/**
 * InputSecret - Generalized secret/password input with eye toggle
 *
 * Reusable across: passwords, API keys, webhook headers, tokens, etc.
 */

import { cn, Input } from '@heltarchat/ui';
import React, { ChangeEventHandler, memo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

interface InputSecretProps {
  value?: string | number | readonly string[];
  onChange?: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  id?: string;
  required?: boolean;
  leftIcon?: React.ReactNode;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

export const InputSecret = memo(function InputSecret({
  value,
  onChange,
  placeholder = 'Enter value',
  className,
  inputClassName,
  id,
  required,
  leftIcon,
  inputRef,
}: InputSecretProps) {
  const [visible, setVisible] = useState<boolean>(false);

  return (
    <div className={cn('relative', className)}>
      {leftIcon && (
        <div className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--heltar-text-secondary)]'>
          {leftIcon}
        </div>
      )}
      <Input
        id={id}
        type={visible ? 'text' : 'password'}
        placeholder={placeholder}
        className={cn('pr-10', leftIcon && 'pl-10', inputClassName)}
        value={value}
        onChange={onChange}
        ref={inputRef}
        required={required}
      />
      <button
        type='button'
        onClick={() => setVisible(prev => !prev)}
        className='absolute right-3 top-1/2 -translate-y-1/2 hover:text-gray-600 transition-colors'
      >
        {visible ? (
          <EyeOff className='h-4 w-4 text-[var(--heltar-text-secondary)]' />
        ) : (
          <Eye className='h-4 w-4 text-[var(--heltar-text-secondary)]' />
        )}
      </button>
    </div>
  );
});
